<template>
  <div dir="rtl" class="mr-8 px-4 sm:px-6 lg:px-8">
    <!-- Alert -->
    <div v-if="alert.show" 
         :class="[
           'mb-6 p-4 rounded-md border',
           alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : '',
           alert.type === 'danger' ? 'bg-red-50 border-red-200 text-red-800' : '',
           alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : ''
         ]">
      {{ alert.message }}
    </div>

    <!-- Page Header -->
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">إدارة المناطق</h1>
        <p class="mt-2 text-sm text-gray-700">إدارة المناطق الجغرافية</p>
      </div>
      <div class="mt-4 sm:mt-0 sm:mr-16 sm:flex-none">
        <button
          @click="openAddModal"
          class="px-4 py-2 bg-customHafadhColor text-white rounded hover:bg-customHafadhColor/90 transition-colors">
          إضافة منطقة جديدة
        </button>
      </div>
    </div>

    <!-- Search Section -->
    <div class="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
      <div class="relative">
        <input
          v-model="searchTerm"
          type="text"
          placeholder="البحث في المناطق..."
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-3"
        />
      </div>
      <div class="text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg flex items-center">
        إجمالي المناطق: {{ regions.length }}
      </div>
    </div>

    <!-- Regions Table -->
    <div class="mt-8 flex flex-col">
      <div class="sm:flex-auto">
        <h1 class="text-lg font-medium text-gray-900">قائمة المناطق</h1>
      </div>

      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="mt-2 text-gray-600">جاري التحميل...</p>
      </div>

      <div v-else-if="filteredRegions.length === 0" class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <p class="mt-2 text-gray-600">
          {{ searchTerm ? 'لا توجد مناطق تطابق البحث' : 'لا توجد مناطق مسجلة' }}
        </p>
      </div>

      <div v-else class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
          <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table class="min-w-full divide-y divide-customHafadhColor">
              <thead class="bg-customHafadhColor text-center">
                <tr>
                  <th class="py-3.5 pr-4 pl-3 text-right text-sm font-semibold text-gray-900 sm:pr-6">#</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">اسم المنطقة (عربي)</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">اسم المنطقة (إنجليزي)</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">رمز المنطقة</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">رمز الدولة</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">تعديل</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">حذف</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr v-for="(region, index) in filteredRegions" :key="region.id">
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm text-gray-900 sm:pr-6">{{ index + 1 }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">{{ region.regionNameAr || '-' }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ region.regionNameEn || '-' }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ region.regionCode || '-' }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ region.countryCode || '-' }}</td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button class="enabled-button" @click="openEditModal(region)">تعديل</button>
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button 
                      class="text-red-600 hover:text-red-800" 
                      @click="handleDelete(region)">
                      حذف
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.totalPages > 1" class="mt-6 flex items-center justify-between">
        <div class="text-sm text-gray-700">
          عرض {{ pagination.from }} إلى {{ pagination.to }} من {{ pagination.total }} نتائج
        </div>
        <div class="flex gap-2">
          <button
            @click="changePage(pagination.currentPage - 1)"
            :disabled="pagination.currentPage === 1"
            class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
            السابق
          </button>
          <button
            @click="changePage(pagination.currentPage + 1)"
            :disabled="pagination.currentPage === pagination.totalPages"
            class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
            التالي
          </button>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="showModal" class="popup" dir="rtl">
      <div class="popup-content" style="max-width: 600px;">
        <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
          {{ editMode ? 'تعديل المنطقة' : 'إضافة منطقة جديدة' }}
        </h3>
        
        <form @submit.prevent="handleSave">
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label for="region_name_ar" class="block text-sm font-medium text-gray-700 mb-2">اسم المنطقة (عربي) *</label>
              <input
                v-model="formData.region_name_ar"
                type="text"
                id="region_name_ar"
                required
                placeholder="أدخل اسم المنطقة بالعربية"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label for="region_name_en" class="block text-sm font-medium text-gray-700 mb-2">اسم المنطقة (إنجليزي)</label>
              <input
                v-model="formData.region_name_en"
                type="text"
                id="region_name_en"
                placeholder="أدخل اسم المنطقة بالإنجليزية"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label for="region_code" class="block text-sm font-medium text-gray-700 mb-2">رمز المنطقة *</label>
              <input
                v-model="formData.region_code"
                type="text"
                id="region_code"
                required
                placeholder="أدخل رمز المنطقة"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label for="country_code" class="block text-sm font-medium text-gray-700 mb-2">رمز الدولة *</label>
              <input
                v-model="formData.country_code"
                type="text"
                id="country_code"
                required
                placeholder="أدخل رمز الدولة (مثل: SA)"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>

          <div class="mt-6 flex gap-3 justify-end">
            <button
              type="button"
              @click="closeModal"
              class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
              إلغاء
            </button>
            <button
              type="submit"
              :disabled="saving"
              class="px-4 py-2 bg-customHafadhColor text-white rounded-md text-sm font-medium hover:bg-customHafadhColor/90 disabled:opacity-50">
              {{ saving ? 'جاري الحفظ...' : (editMode ? 'تحديث' : 'إضافة') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import axios from 'axios'
import url from '@/url.js'

export default defineComponent({
  name: 'RegionsManagement',
  data() {
    return {
      regions: [],
      loading: false,
      saving: false,
      searchTerm: '',
      showModal: false,
      editMode: false,
      formData: {
        id: null,
        region_name_ar: '',
        region_name_en: '',
        region_code: '',
        country_code: '',
      },
      alert: {
        show: false,
        type: 'success',
        message: '',
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        total: 0,
        from: 0,
        to: 0,
      },
    }
  },
  computed: {
    filteredRegions() {
      let filtered = this.regions

      if (this.searchTerm) {
        const search = this.searchTerm.toLowerCase()
        filtered = filtered.filter(region => 
          region.regionNameAr?.toLowerCase().includes(search) ||
          region.regionNameEn?.toLowerCase().includes(search) ||
          region.regionCode?.toLowerCase().includes(search) ||
          region.countryCode?.toLowerCase().includes(search)
        )
      }

      return filtered
    }
  },
  watch: {
    searchTerm() {
      this.pagination.currentPage = 1
    },
  },
  mounted() {
    this.loadRegions()
  },
  methods: {
    async loadRegions() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage,
          size: 20,
        }
        if (this.searchTerm) {
          params.search = this.searchTerm
        }

        const { data } = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.regions_list,
          {
            params,
            headers: { 'X-Authorization': localStorage.getItem('token') }
          }
        )

        // Handle nested response structure: data.result.data.data and data.result.data.pagination
        if (data.result && data.result.data) {
          if (Array.isArray(data.result.data)) {
            // Direct array response
            this.regions = data.result.data
          } else if (data.result.data.data && Array.isArray(data.result.data.data)) {
            // Nested structure: result.data.data
            this.regions = data.result.data.data
            if (data.result.data.pagination) {
              this.pagination = {
                currentPage: data.result.data.pagination.current_page || 1,
                totalPages: data.result.data.pagination.total_pages || 1,
                total: data.result.data.pagination.total || 0,
                from: data.result.data.pagination.from || 0,
                to: data.result.data.pagination.to || 0,
              }
            }
          } else {
            this.regions = []
          }
        } else {
          this.regions = []
        }
      } catch (error) {
        console.error('Failed to load regions', error)
        this.showAlert('danger', 'فشل في تحميل المناطق')
        this.regions = []
      } finally {
        this.loading = false
      }
    },
    openAddModal() {
      this.editMode = false
      this.formData = {
        id: null,
        region_name_ar: '',
        region_name_en: '',
        region_code: '',
        country_code: '',
      }
      this.showModal = true
    },
    openEditModal(region) {
      this.editMode = true
      this.formData = {
        id: region.id,
        region_name_ar: region.regionNameAr || '',
        region_name_en: region.regionNameEn || '',
        region_code: region.regionCode || '',
        country_code: region.countryCode || '',
      }
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
      this.editMode = false
      this.formData = {
        id: null,
        region_name_ar: '',
        region_name_en: '',
        region_code: '',
        country_code: '',
      }
    },
    async handleSave() {
      this.saving = true
      try {
        const payload = {
          region_name_ar: this.formData.region_name_ar,
          region_name_en: this.formData.region_name_en || null,
          region_code: this.formData.region_code,
          country_code: this.formData.country_code,
        }

        if (this.editMode) {
          await axios.put(
            process.env.VUE_APP_SERVICE_URL + url.regions_update + this.formData.id,
            payload,
            { headers: { 'X-Authorization': localStorage.getItem('token') } }
          )
          this.showAlert('success', 'تم تحديث المنطقة بنجاح')
        } else {
          await axios.post(
            process.env.VUE_APP_SERVICE_URL + url.regions_create,
            payload,
            { headers: { 'X-Authorization': localStorage.getItem('token') } }
          )
          this.showAlert('success', 'تم إضافة المنطقة بنجاح')
        }

        this.closeModal()
        this.loadRegions()
      } catch (error) {
        console.error('Failed to save region', error)
        const message = error.response?.data?.result?.mesAr || error.response?.data?.mesAr || 'فشل في حفظ المنطقة'
        this.showAlert('danger', message)
      } finally {
        this.saving = false
      }
    },
    async handleDelete(region) {
      if (!confirm(`هل أنت متأكد من حذف المنطقة "${region.regionNameAr || region.regionCode}"؟`)) {
        return
      }

      try {
        await axios.delete(
          process.env.VUE_APP_SERVICE_URL + url.regions_delete + region.id,
          { headers: { 'X-Authorization': localStorage.getItem('token') } }
        )
        this.showAlert('success', 'تم حذف المنطقة بنجاح')
        this.loadRegions()
      } catch (error) {
        console.error('Failed to delete region', error)
        const message = error.response?.data?.result?.mesAr || error.response?.data?.mesAr || 'فشل في حذف المنطقة'
        this.showAlert('danger', message)
      }
    },
    changePage(page) {
      if (page >= 1 && page <= this.pagination.totalPages) {
        this.pagination.currentPage = page
        this.loadRegions()
      }
    },
    showAlert(type, message) {
      this.alert = { show: true, type, message }
      setTimeout(() => {
        this.alert.show = false
      }, 5000)
    },
  },
})
</script>

<style scoped>
.popup {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.popup-content {
  background: white;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.enabled-button {
  color: #3b82f6;
  font-weight: 500;
  cursor: pointer;
}

.enabled-button:hover {
  color: #2563eb;
  text-decoration: underline;
}
</style>

